import { solveCramer2 } from './solvers.ts';

const G = 9.80665;

export function rpConstants(
  m0: number, m1: number, L0: number, L1: number, a0: number,
) {
  const b0 = L0 - a0;
  const a1 = L1;
  const I0 = m0 * a0 * a0 + b0 * b0 * m1;
  const I1 = m1 * a1 * a1;
  const mu0 = m0 * a0 - b0 * m1;
  const mu1 = m1 * a1;
  const M01 = -b0 * mu1;
  return { I0, I1, mu0, mu1, M01, b0, a1 };
}

export function resonantDerivatives(
  theta0: number, omega0: number, theta1: number, omega1: number,
  m0: number, m1: number, L0: number, L1: number, a0: number,
): { dTheta0: number; dOmega0: number; dTheta1: number; dOmega1: number } {
  const c = rpConstants(m0, m1, L0, L1, a0);

  const c01 = Math.cos(theta0 - theta1);
  const s01 = Math.sin(theta0 - theta1);

  const A00 = c.I0;
  const A01 = c.M01 * c01;
  const A11 = c.I1;

  const f0 = -G * c.mu0 * Math.sin(theta0) - c.M01 * s01 * omega1 * omega1;
  const f1 = -G * c.mu1 * Math.sin(theta1) + c.M01 * s01 * omega0 * omega0;

  const [al0, al1] = solveCramer2(A00, A01, A01, A11, f0, f1);

  return { dTheta0: omega0, dOmega0: al0, dTheta1: omega1, dOmega1: al1 };
}

export function resonantTip(
  t0: number, t1: number,
  m0: number, m1: number, L0: number, L1: number, a0: number,
): { x: number; y: number } {
  const c = rpConstants(m0, m1, L0, L1, a0);
  const ax = -c.b0 * Math.sin(t0);
  const ay = c.b0 * Math.cos(t0);
  return { x: ax + c.a1 * Math.sin(t1), y: ay - c.a1 * Math.cos(t1) };
}

export function resonantNodes(
  t0: number, t1: number,
  m0: number, m1: number, L0: number, L1: number, a0: number,
): Array<{ x: number; y: number }> {
  const c = rpConstants(m0, m1, L0, L1, a0);
  const nodes: Array<{ x: number; y: number }> = [];
  nodes.push({ x: a0 * Math.sin(t0), y: -a0 * Math.cos(t0) });
  const ax = -c.b0 * Math.sin(t0);
  const ay = c.b0 * Math.cos(t0);
  nodes.push({ x: ax + c.a1 * Math.sin(t1), y: ay - c.a1 * Math.cos(t1) });
  return nodes;
}
